import { BIN_ADJUST_REASON_LABELS, type CodeSuggestion, type Coordinates3D, type MapBinDetail, type MapBinItem, type MapBinStock, type WarehouseMap } from '@wms/domain';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { freshLine } from '../binStock';
import { fmt, swatch } from '../format';
import { useRoute } from '../router';
import { createScanRecorder } from '../scan';
import { store, useSession } from '../session';
import { typedByPerson, type SuggestSource } from '../suggest';
import { Empty, ErrorBanner, ProductThumb, SellerChip, Spinner, SuggestList, useCodeSuggestions, useFloorAction, useLoad, useToast } from '../ui';
import { Icon } from '../ui/icons';
import { buildWorld, type Cell, type World } from '../ui/map/geometry';
import { MapView, type CellPaint, type MapHit } from '../ui/map/MapView';
import { AdjustDialog, MoveDialog } from './BinStockDialogs';

type ColorMode = 'fill' | 'seller' | 'expiry';
const MODE_KEY = 'wms.floorMap.mode';
const MODES: readonly { readonly id: ColorMode; readonly label: string }[] = [
  { id: 'fill', label: 'Doluluk' },
  { id: 'seller', label: 'Satıcı' },
  { id: 'expiry', label: 'Son kullanma' },
];

/** What lies on one shelf (or one rack position on the floor plan), summed over its bins. */
interface ShelfStock {
  readonly onHand: number;
  readonly used: number;
  /** capacity of the shelf's bins that have one */
  readonly capacity: number;
  readonly tenants: readonly string[];
  readonly nearestExpiry: string | null;
}

const isStorage = (cell: Cell) => cell.type === 'pick' || cell.type === 'bulk';
/** what the search box offers a manager typing: products to search for, bins to open */
const MAP_SUGGEST: SuggestSource = { kinds: ['product', 'bin'] };
const ratioOf = (s: ShelfStock) => (s.capacity > 0 && s.used > 0 ? s.used / s.capacity : null);

function fillColor(s: ShelfStock): string {
  const r = ratioOf(s);
  if (r === null) return 'var(--occ-unknown)';
  return r < 0.5 ? 'var(--occ-low)' : r < 0.9 ? 'var(--occ-mid)' : 'var(--occ-high)';
}

function expiryColor(s: ShelfStock): string {
  if (!s.nearestExpiry) return 'var(--occ-unknown)';
  const days = fmt.daysLeft(s.nearestExpiry);
  return days < 0 ? 'var(--map-short)' : days <= 30 ? 'var(--occ-high)' : days <= 90 ? 'var(--occ-mid)' : 'var(--occ-low)';
}

/**
 * The whole warehouse to look around in: shelves coloured by how full they are, whose stock
 * lies on them or how soon it expires; a search shows where a product lies; a tap opens the
 * shelf's bins with product, lot, expiry and quantity.
 */
export function FloorMap() {
  const { warehouseId, warehouses, isAdmin } = useSession();
  const route = useRoute();
  const [mode, setMode] = useState<ColorMode>(() => {
    const saved = store.get(MODE_KEY);
    return saved === 'seller' || saved === 'expiry' ? saved : 'fill';
  });
  const linked = route.query.get('q');
  const [search, setSearch] = useState(linked ?? '');
  const [term, setTerm] = useState(search.trim());
  // a manager typing in the search box is offered products and bins: a product is searched, a bin's shelf opens
  const searchTrace = useRef(createScanRecorder());
  const suggestions = useCodeSuggestions({
    source: MAP_SUGGEST,
    enabled: true,
    value: search,
    byPerson: () => typedByPerson(searchTrace.current.trace(), search),
  });
  // "Haritada" on the stock list links here with the SKU (nobody typed it: no suggestions for it)
  useEffect(() => {
    if (linked === null) return;
    searchTrace.current.clear();
    setSearch(linked);
  }, [linked]);
  const [selected, setSelected] = useState<Cell | null>(null);
  const [seller, setSeller] = useState<string | null>(null);
  // what the map frames: the whole warehouse, the search hits or a shelf picked from a list
  const [view, setView] = useState<{ key: string; points: readonly Coordinates3D[] }>({ key: 'all', points: [] });

  const plan = useLoad(() => (warehouseId ? api<WarehouseMap>('GET', `/api/wms/map?warehouseId=${warehouseId}`) : Promise.resolve(undefined)), [warehouseId]);
  const stock = useLoad(
    () => (warehouseId ? api<MapBinStock[]>('GET', `/api/wms/map/stock?warehouseId=${warehouseId}`) : Promise.resolve(undefined)),
    [warehouseId],
    60_000,
  );
  const hits = useLoad(
    () =>
      warehouseId && term
        ? api<MapBinStock[]>('GET', `/api/wms/map/stock?warehouseId=${warehouseId}&q=${encodeURIComponent(term)}`)
        : Promise.resolve(undefined),
    [warehouseId, term],
  );

  // the search runs once typing stops
  useEffect(() => {
    const next = search.trim();
    if (next === term) return;
    const timer = setTimeout(() => setTerm(next), 350);
    return () => clearTimeout(timer);
  }, [search, term]);

  const world = useMemo(() => (plan.data ? buildWorld(plan.data) : null), [plan.data]);
  const binStock = useMemo(() => new Map((stock.data ?? []).map((b) => [b.code, b])), [stock.data]);
  const capacity = useMemo(() => new Map((plan.data?.bins ?? []).map((b) => [b.code, b.maxVolumeCm3])), [plan.data]);
  const hitCodes = useMemo(() => (hits.data ? new Set(hits.data.map((b) => b.code)) : null), [hits.data]);

  const shelfStock = useCallback(
    (cell: Cell): ShelfStock | null => {
      let onHand = 0;
      let used = 0;
      let cap = 0;
      let nearestExpiry: string | null = null;
      const tenants = new Set<string>();
      for (const code of cell.codes) {
        cap += capacity.get(code) ?? 0;
        const b = binStock.get(code);
        if (!b) continue;
        onHand += b.onHand;
        used += b.usedVolumeCm3;
        for (const t of b.tenants) tenants.add(t);
        if (b.nearestExpiry && (!nearestExpiry || b.nearestExpiry < nearestExpiry)) nearestExpiry = b.nearestExpiry;
      }
      return onHand > 0 ? { onHand, used, capacity: cap, tenants: [...tenants].sort(), nearestExpiry } : null;
    },
    [binStock, capacity],
  );

  const sellers = useMemo(() => [...new Set((stock.data ?? []).flatMap((b) => b.tenants))].sort(), [stock.data]);

  const paint = useCallback(
    (cell: Cell): CellPaint | undefined => {
      const classes: string[] = [];
      if (selected && (cell.key === selected.key || cell.key === selected.column || cell.column === selected.key)) classes.push('selected');
      const s = shelfStock(cell);
      const hit = hitCodes ? cell.codes.some((c) => hitCodes.has(c)) : false;
      if (hitCodes && isStorage(cell)) classes.push(hit ? 'hit' : 'dim');
      if (!hitCodes && mode === 'seller' && seller && isStorage(cell) && !s?.tenants.includes(seller)) classes.push('dim');
      let fill: string | undefined;
      if (hit) fill = 'var(--brand)';
      else if (s && isStorage(cell)) {
        if (mode === 'fill') fill = fillColor(s);
        else if (mode === 'expiry') fill = expiryColor(s);
        else fill = s.tenants.length === 1 ? swatch(s.tenants[0]!) : 'var(--occ-mixed)';
      }
      const ratio = s ? ratioOf(s) : null;
      const title = [
        cell.label,
        s ? `${fmt.n(s.onHand)} adet` : isStorage(cell) ? 'boş' : null,
        ratio !== null ? `%${Math.round(ratio * 100)} dolu` : null,
        s?.tenants.length ? s.tenants.join(', ') : null,
        s?.nearestExpiry ? `SKT ${fmt.date(s.nearestExpiry)}` : null,
        cell.blocked ? 'sayım için kilitli' : null,
      ]
        .filter(Boolean)
        .join(' · ');
      return { fill, className: classes.join(' ') || undefined, title };
    },
    [selected, shelfStock, hitCodes, mode, seller],
  );

  // new search results: show where they lie
  const hitPoints = useMemo(() => {
    if (!world || !hitCodes) return [];
    return world.cells.filter((c) => c.codes.some((code) => hitCodes.has(code))).flatMap(corners);
  }, [world, hitCodes]);
  // only when a search answers, not every time the map is redrawn
  useEffect(() => {
    if (term && hits.data) setView({ key: `q:${term}:${hits.data.length}`, points: hitPoints });
  }, [hits.data]);

  const kpis = useMemo(() => {
    if (!plan.data) return null;
    const storage = plan.data.bins.filter((b) => b.type === 'pick' || b.type === 'bulk');
    const filled = storage.filter((b) => binStock.has(b.code));
    const used = filled.reduce((n, b) => n + (b.maxVolumeCm3 ? binStock.get(b.code)!.usedVolumeCm3 : 0), 0);
    const cap = storage.reduce((n, b) => n + (b.maxVolumeCm3 ?? 0), 0);
    const expiring = [...binStock.values()].filter((b) => b.nearestExpiry && fmt.daysLeft(b.nearestExpiry) <= 30).length;
    return {
      bins: storage.length,
      filled: filled.length,
      blocked: storage.filter((b) => b.isBlocked).length,
      fill: cap > 0 ? used / cap : null,
      expiring,
    };
  }, [plan.data, binStock]);

  const onTap = (hit: MapHit | null) => setSelected(hit?.kind === 'cell' ? hit.cell : null);
  const pick = (cell: Cell) => {
    setSelected(cell);
    setView({ key: `cell:${cell.key}:${Date.now()}`, points: corners(cell) });
  };
  const pickSuggestion = (s: CodeSuggestion) => {
    searchTrace.current.clear();
    if (s.kind === 'bin') {
      // the bin's shelf opens, framed on the map
      suggestions.close('');
      setSearch('');
      setTerm('');
      const cell = world?.cells.find((c) => c.codes.includes(s.code));
      if (cell) pick(cell);
      return;
    }
    // the list closes for the picked text too (a tap keeps the cursor in the field: no blur closes it)
    const text = s.kind === 'product' ? s.sku : s.code;
    suggestions.close(text);
    setSearch(text);
    setTerm(text);
  };

  if (!warehouseId) return <Empty title="Depo seçilmedi" />;
  if (plan.error) return <ErrorBanner message={plan.error} onRetry={() => void plan.reload()} />;
  if (!world || !plan.data) return <Spinner />;

  const warehouse = warehouses.find((w) => w.id === warehouseId);

  return (
    <>
      <div className="row floor-bar">
        <label className="search-box">
          <Icon name="search" size={18} />
          <input
            className="input"
            placeholder="SKU, ürün adı veya barkod ara"
            value={search}
            {...suggestions.inputProps}
            onChange={(e) => {
              searchTrace.current.change(e);
              setSearch(e.target.value);
            }}
            onKeyDown={(e) => {
              searchTrace.current.key(e);
              if (suggestions.onKey(e, pickSuggestion)) return;
              if (e.key === 'Enter') setTerm(search.trim());
            }}
            onBlur={() => suggestions.open && suggestions.close()}
            aria-label="Haritada ürün ara"
          />
          <SuggestList suggestions={suggestions} onPick={pickSuggestion} />
          {search && (
            <button
              className="btn sm ghost"
              onClick={() => {
                setSearch('');
                setTerm('');
              }}
              aria-label="Aramayı temizle"
            >
              <Icon name="x" size={16} />
            </button>
          )}
        </label>
        <div className="seg" role="group" aria-label="Rafları neye göre renklendir">
          {MODES.map((m) => (
            <button
              key={m.id}
              className={mode === m.id ? 'on' : ''}
              aria-pressed={mode === m.id}
              onClick={() => {
                setMode(m.id);
                store.set(MODE_KEY, m.id);
              }}
            >
              {m.label}
            </button>
          ))}
        </div>
        {kpis && (
          <span className="muted">
            {fmt.n(kpis.filled)} / {fmt.n(kpis.bins)} göz dolu{kpis.fill !== null && ` · ortalama %${Math.round(kpis.fill * 100)}`}
            {kpis.blocked > 0 && ` · ${kpis.blocked} kilitli`}
          </span>
        )}
      </div>

      <div className="floor-layout">
        <section className="card map-card floor-map">
          <div className="card-h">
            <Icon name="map" />
            <h2>{warehouse?.name ?? 'Depo haritası'}</h2>
            <span className="sub">{term ? (hits.loading || !hits.data ? 'Aranıyor…' : `${hits.data.length} gözde bulundu`) : 'Bir rafa dokunun, içindekiler listelenir'}</span>
          </div>
          {stock.error && <ErrorBanner message={stock.error} onRetry={() => void stock.reload()} />}
          <MapView
            world={world}
            label="Depo haritası"
            paint={paint}
            depot={plan.data.layout.depot ?? null}
            frame={view.points}
            frameKey={view.key}
            frames={hitCodes ? [{ label: 'Bulunanlar', title: 'Aranan ürünün bulunduğu gözler', points: hitPoints }] : []}
            onTap={onTap}
            legend={<Legend mode={hitCodes ? null : mode} sellers={sellers} seller={seller} onSeller={setSeller} />}
          />
        </section>

        <section className="card floor-panel">
          {selected ? (
            <ShelfPanel
              warehouseId={warehouseId}
              cell={selected}
              onClose={() => setSelected(null)}
              // the manager moved or corrected stock: the map's colours and the search follow
              onChanged={() => {
                void stock.reload();
                if (term) void hits.reload();
              }}
            />
          ) : term ? (
            <SearchResults
              term={term}
              hits={hits.data}
              error={hits.error}
              world={world}
              onPick={pick}
            />
          ) : (
            <Overview kpis={kpis} manager={isAdmin} />
          )}
        </section>
      </div>
    </>
  );
}

const corners = (c: Cell): Coordinates3D[] => [c.z0, c.z1].flatMap((z) => [c.x0, c.x1].flatMap((x) => [c.y0, c.y1].map((y) => ({ x, y, z }))));

function Legend(props: { mode: ColorMode | null; sellers: readonly string[]; seller: string | null; onSeller: (code: string | null) => void }) {
  const dot = (color: string, text: string) => (
    <span key={text}>
      <i style={{ background: color }} /> {text}
    </span>
  );
  if (props.mode === null) return <>{[dot('var(--brand)', 'Aranan ürün'), dot('var(--rack-top)', 'Diğer raflar')]}</>;
  if (props.mode === 'fill')
    return <>{[dot('var(--occ-low)', '%50 altı'), dot('var(--occ-mid)', '%50–90'), dot('var(--occ-high)', '%90 üstü'), dot('var(--occ-unknown)', 'Hacmi bilinmiyor'), dot('var(--rack-top)', 'Boş')]}</>;
  if (props.mode === 'expiry')
    return <>{[dot('var(--map-short)', 'Süresi geçmiş'), dot('var(--occ-high)', '30 gün içinde'), dot('var(--occ-mid)', '90 gün içinde'), dot('var(--occ-low)', '90 günden uzun'), dot('var(--occ-unknown)', 'SKT yok')]}</>;
  return (
    <>
      {props.sellers.map((code) => (
        <button
          key={code}
          className={`legend-btn${props.seller === code ? ' on' : ''}`}
          onClick={() => props.onSeller(props.seller === code ? null : code)}
          title={props.seller === code ? 'Tüm satıcıları göster' : `Yalnız ${code} stoğunu öne çıkar`}
        >
          <i style={{ background: swatch(code) }} /> {code}
        </button>
      ))}
      {dot('var(--occ-mixed)', 'Birden çok satıcı')}
    </>
  );
}

function Overview({ kpis, manager }: { kpis: { bins: number; filled: number; blocked: number; fill: number | null; expiring: number } | null; manager: boolean }) {
  return (
    <div className="card-b stack">
      <h3>Depoya genel bakış</h3>
      {kpis && (
        <div className="kpi-grid">
          <div>
            <b>{fmt.n(kpis.bins)}</b>
            <span>raf gözü</span>
          </div>
          <div>
            <b>{fmt.n(kpis.filled)}</b>
            <span>stoklu göz</span>
          </div>
          <div>
            <b>{kpis.fill === null ? '—' : `%${Math.round(kpis.fill * 100)}`}</b>
            <span>hacim doluluğu</span>
          </div>
          <div>
            <b>{fmt.n(kpis.expiring)}</b>
            <span>gözde SKT ≤ 30 gün</span>
          </div>
          {kpis.blocked > 0 && (
            <div>
              <b>{fmt.n(kpis.blocked)}</b>
              <span>sayım için kilitli</span>
            </div>
          )}
        </div>
      )}
      <p className="muted">
        Haritayı sürükleyerek döndürün ve eğin; bir rafa dokunduğunuzda gözlerindeki ürünler, lotlar ve son kullanma tarihleri burada
        görünür. Bir ürünü aramak için yukarıdaki kutuya SKU, ürün adı veya barkod yazın.
      </p>
      {manager && (
        <p className="muted">
          Depo yöneticisi olarak bir ürünün satırındaki “Taşı” ile stoğu başka bir göze taşıyabilir, “Sayıyı düzelt” ile gözdeki adedi rafta
          saydığınıza göre düzeltebilirsiniz.
        </p>
      )}
    </div>
  );
}

function SearchResults(props: { term: string; hits: readonly MapBinStock[] | undefined; error: string | null; world: World; onPick: (cell: Cell) => void }) {
  const { term, hits, error, world, onPick } = props;
  if (error) return <ErrorBanner message={error} />;
  if (!hits) return <Spinner />;
  if (hits.length === 0)
    return (
      <div className="card-b">
        <Empty title={`"${term}" hiçbir gözde yok`}>Stokta olmayan ya da yalnız kabul alanında bekleyen ürünler haritada görünmez.</Empty>
      </div>
    );
  const cellOf = (code: string) => world.cells.find((c) => c.codes.includes(code)) ?? null;
  const total = hits.reduce((n, h) => n + h.onHand, 0);
  return (
    <div className="card-b stack">
      <h3>
        "{term}" · {fmt.n(total)} adet, {hits.length} göz
      </h3>
      <ul className="hit-list">
        {hits.map((h) => {
          const cell = cellOf(h.code);
          return (
            <li key={h.code}>
              <button className="hit-row" onClick={() => cell && onPick(cell)} disabled={!cell}>
                <span className="mono">{h.code}</span>
                <span className="row" style={{ gap: 4 }}>
                  {h.tenants.map((t) => (
                    <SellerChip key={t} code={t} />
                  ))}
                </span>
                {h.nearestExpiry && <span className={`chip ${fmt.daysLeft(h.nearestExpiry) < 90 ? 'warn' : ''}`}>SKT {fmt.date(h.nearestExpiry)}</span>}
                <b className="num">{fmt.n(h.onHand)}</b>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** A bin line the manager is moving or correcting. */
interface LineAction {
  readonly kind: 'move' | 'adjust';
  readonly bin: MapBinDetail;
  readonly item: MapBinItem;
}

function ShelfPanel({ warehouseId, cell, onClose, onChanged }: { warehouseId: string; cell: Cell; onClose: () => void; onChanged: () => void }) {
  // "Taşı" and "Sayıyı düzelt" are the warehouse manager's (depo yöneticisi) alone
  const { isAdmin } = useSession();
  const toast = useToast();
  const detail = useLoad(
    () => api<MapBinDetail[]>('GET', `/api/wms/map/bins?warehouseId=${warehouseId}&codes=${encodeURIComponent(cell.codes.join(','))}`),
    [warehouseId, cell.key],
  );
  const changed = () => {
    void detail.reload();
    onChanged();
  };
  // a press whose answer was lost is sent again with its key; given up on, the bins are read again
  const fix = useFloorAction({ onUnsure: changed });
  const [action, setAction] = useState<LineAction | null>(null);
  useEffect(() => setAction(null), [cell.key]);
  // the line as the panel has it now: a refusal that said the figures were out of date reads the bin again
  const line = action && detail.data ? freshLine(detail.data, action.bin, action.item) : action;
  const kind = cell.type === 'bulk' ? 'Yedek alan (palet)' : cell.type === 'pick' ? 'Toplama rafı' : 'Alan';
  return (
    <div className="card-b stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div>
          <h3 className="mono">{cell.label}</h3>
          <div className="muted">
            {kind} · {cell.codes.length} göz
          </div>
        </div>
        <button className="btn sm ghost" onClick={onClose} aria-label="Kapat">
          <Icon name="x" size={16} />
        </button>
      </div>
      {cell.blocked && (
        <div className="banner bad">
          <div>
            Sayım için kilitli göz var: sayılana kadar yeni rezervasyonlar ve yerleştirme bu gözleri atlar; açık rotalardaki duraklar sürer.{' '}
            <a href="#/sayim">Sayım ekranına git</a>
          </div>
        </div>
      )}
      {fix.banner}
      {detail.error && <ErrorBanner message={detail.error} onRetry={() => void detail.reload()} />}
      {!detail.data ? (
        <Spinner />
      ) : (
        detail.data.map((bin) => (
          <BinContents key={bin.code} bin={bin} onAction={isAdmin ? (kind, item) => setAction({ kind, bin, item }) : null} actionsWait={fix.waiting} />
        ))
      )}
      {action?.kind === 'move' && line && (
        <MoveDialog
          warehouseId={warehouseId}
          bin={line.bin}
          item={line.item}
          runner={fix}
          onClose={() => setAction(null)}
          onStale={changed}
          onMoved={(r) => {
            setAction(null);
            toast(`${fmt.n(r.qty)} adet ${r.sku}: ${r.fromBin} → ${r.toBin}${r.capacityAccepted ? ' (sığdı onayıyla)' : ''}`, 'ok');
            changed();
          }}
        />
      )}
      {action?.kind === 'adjust' && line && (
        <AdjustDialog
          warehouseId={warehouseId}
          bin={line.bin}
          item={line.item}
          runner={fix}
          onClose={() => setAction(null)}
          onStale={changed}
          onSaved={(r) => {
            setAction(null);
            toast(`${r.bin} · ${r.sku}: ${fmt.n(r.before)} → ${fmt.n(r.after)} (${BIN_ADJUST_REASON_LABELS[r.reason]})`, 'ok');
            changed();
          }}
        />
      )}
    </div>
  );
}

function BinContents(props: {
  bin: MapBinDetail;
  /** the manager's "Taşı" / "Sayıyı düzelt" on each line; null for everyone else */
  onAction: ((kind: LineAction['kind'], item: MapBinItem) => void) | null;
  /** a press whose answer was lost waits to be sent again */
  actionsWait: boolean;
}) {
  const { bin, onAction } = props;
  const ratio = bin.maxVolumeCm3 ? bin.usedVolumeCm3 / bin.maxVolumeCm3 : null;
  // goods in other areas move by their own work (putaway, packing, returns); a counted bin waits for its count
  const storage = bin.type === 'pick' || bin.type === 'bulk';
  const actions = onAction !== null && storage && !bin.isBlocked;
  return (
    <div className="bin-box">
      <div className="bin-head">
        <span className="mono">{bin.code}</span>
        {bin.isBlocked && <span className="chip bad">Kilitli</span>}
        {ratio !== null && (
          <span className="fill-bar" title={`${fmt.n(Math.round(bin.usedVolumeCm3 / 1000))} / ${fmt.n(Math.round(bin.maxVolumeCm3! / 1000))} litre`}>
            <i style={{ width: `${Math.min(100, ratio * 100)}%`, background: ratio < 0.5 ? 'var(--occ-low)' : ratio < 0.9 ? 'var(--occ-mid)' : 'var(--occ-high)' }} />
          </span>
        )}
        {ratio !== null && <span className="muted">%{Math.round(ratio * 100)}</span>}
      </div>
      {onAction !== null && storage && bin.isBlocked && bin.items.length > 0 && (
        <div className="muted bin-note">Sayım bitene kadar bu gözde taşıma ve sayı düzeltme yapılamaz.</div>
      )}
      {bin.items.length === 0 ? (
        <div className="muted">Boş</div>
      ) : (
        <ul className="bin-items">
          {bin.items.map((item, i) => (
            <li key={`${item.productId}-${item.lotNo ?? ''}-${item.expiryDate ?? ''}-${i}`}>
              <ProductThumb name={item.name} imageUrl={item.imageUrl} small />
              <div className="what">
                <div className="name">{item.name}</div>
                <div className="muted row" style={{ gap: 6 }}>
                  <SellerChip code={item.tenantCode} />
                  <span className="mono">{item.sku}</span>
                  {item.lotNo && <span>Lot {item.lotNo}</span>}
                  {item.expiryDate && (
                    <span className={`chip ${fmt.daysLeft(item.expiryDate) < 0 ? 'bad' : fmt.daysLeft(item.expiryDate) < 90 ? 'warn' : ''}`}>SKT {fmt.date(item.expiryDate)}</span>
                  )}
                </div>
              </div>
              <div className="qty">
                <b>{fmt.n(item.onHand)}</b>
                {item.reserved > 0 && <span className="muted">{fmt.n(item.reserved)} rezerve</span>}
                {item.picked > 0 && <span className="muted">{fmt.n(item.picked)} toplandı</span>}
              </div>
              {actions && (
                <div className="bin-item-actions">
                  <button className="btn sm" disabled={props.actionsWait} onClick={() => onAction?.('move', item)}>
                    <Icon name="move" size={16} /> Taşı
                  </button>
                  <button className="btn sm" disabled={props.actionsWait} onClick={() => onAction?.('adjust', item)}>
                    <Icon name="edit" size={16} /> Sayıyı düzelt
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
